import React, { useState } from 'react';
import { Calculator as CalcIcon, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';

const Calculator = () => {
  const [calculatorData, setCalculatorData] = useState({
    initialAmount: '',
    monthlyAmount: '',
    duration: '',
    interestRate: '',
    calculationType: 'simple'
  });

  const [result, setResult] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCalculatorData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateInterest = () => {
    const initial = parseFloat(calculatorData.initialAmount) || 0;
    const monthly = parseFloat(calculatorData.monthlyAmount) || 0;
    const years = parseFloat(calculatorData.duration) || 0;
    const rate = parseFloat(calculatorData.interestRate) / 100 || 0;

    if (initial <= 0 && monthly <= 0) {
      alert('Veuillez entrer un montant initial ou des versements mensuels');
      return;
    }

    let finalAmount;
    let totalInterest;
    let totalInvested = initial + (monthly * years * 12);

    if (calculatorData.calculationType === 'simple') {
      // Intérêts simples
      const simpleInterest = initial * rate * years;
      const monthlyInterest = (monthly * years * 12) * rate * (years / 2);
      finalAmount = totalInvested + simpleInterest + monthlyInterest;
      totalInterest = simpleInterest + monthlyInterest;
    } else {
      // Intérêts composés
      if (monthly > 0) {
        // Avec versements mensuels
        const monthlyRate = rate / 12;
        const months = years * 12;
        
        // Capital initial avec intérêts composés
        const futureValueInitial = initial * Math.pow(1 + rate, years);
        
        // Annuités avec intérêts composés
        const futureValueAnnuity = monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
        
        finalAmount = futureValueInitial + futureValueAnnuity;
      } else {
        // Capital initial seulement
        finalAmount = initial * Math.pow(1 + rate, years);
      }
      totalInterest = finalAmount - totalInvested;
    }

    // Generate chart data
    const chartData = [];
    for (let year = 0; year <= years; year++) {
      let yearInvested = initial + (monthly * year * 12);
      let yearAmount;
      let yearInterest;
      
      if (calculatorData.calculationType === 'simple') {
        const simpleInterest = initial * rate * year;
        const monthlyInterest = (monthly * year * 12) * rate * (year / 2);
        yearAmount = yearInvested + simpleInterest + monthlyInterest;
        yearInterest = simpleInterest + monthlyInterest;
      } else {
        if (monthly > 0) {
          const monthlyRate = rate / 12;
          const months = year * 12;
          const futureValueInitial = initial * Math.pow(1 + rate, year);
          const futureValueAnnuity = months > 0 ? monthly * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) : 0;
          yearAmount = futureValueInitial + futureValueAnnuity;
        } else {
          yearAmount = initial * Math.pow(1 + rate, year);
        }
        yearInterest = yearAmount - yearInvested;
      }
      
      chartData.push({
        year: year,
        invested: Math.round(yearInvested),
        total: Math.round(yearAmount),
        interest: Math.round(yearInterest)
      });
    }

    setResult({
      finalAmount: Math.round(finalAmount),
      totalInterest: Math.round(totalInterest),
      totalInvested: Math.round(totalInvested),
      roi: Math.round((totalInterest / totalInvested) * 100),
      chartData: chartData
    });
  };

  const resetCalculator = () => {
    setCalculatorData({
      initialAmount: '',
      monthlyAmount: '',
      duration: '',
      interestRate: '',
      calculationType: 'simple'
    });
    setResult(null);
  };

  return (
    <section id="calculateur" className="section section-alt">
      <div className="container">
        <div className="text-center mb-6">
          <h2 className="heading-1 mb-3">Calculateur d'Intérêts</h2>
          <p className="body-large text-muted" style={{ maxWidth: '600px', margin: '0 auto' }}>
            Découvrez le potentiel de croissance de votre épargne avec notre calculateur intégré
          </p>
        </div>

        <div className="calculator-container">
          <div className="flex" style={{ alignItems: 'center', gap: '0.5rem', marginBottom: '2rem' }}>
            <CalcIcon size={24} style={{ color: 'var(--primary-gold)' }} />
            <h3 className="heading-3">Simulez vos gains</h3>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); calculateInterest(); }}>
            {/* Type de calcul */}
            <div className="form-group">
              <label className="form-label">Type de calcul</label>
              <select
                name="calculationType"
                value={calculatorData.calculationType}
                onChange={handleInputChange}
                className="form-input form-select"
              >
                <option value="simple">Intérêts simples</option>
                <option value="compound">Intérêts composés</option>
              </select>
            </div>

            {/* Montant initial */}
            <div className="form-group">
              <label className="form-label">Montant initial (€)</label>
              <input
                type="number"
                name="initialAmount"
                value={calculatorData.initialAmount}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Ex: 10000"
                min="0"
                step="100"
              />
            </div>

            {/* Versements mensuels */}
            <div className="form-group">
              <label className="form-label">Versements mensuels (€)</label>
              <input
                type="number"
                name="monthlyAmount"
                value={calculatorData.monthlyAmount}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Ex: 300"
                min="0"
                step="50"
              />
            </div>

            {/* Durée */}
            <div className="form-group">
              <label className="form-label">Durée d'investissement (années)</label>
              <input
                type="number"
                name="duration"
                value={calculatorData.duration}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Ex: 15"
                min="1"
                max="50"
                required
              />
            </div>

            {/* Taux d'intérêt */}
            <div className="form-group">
              <label className="form-label">Taux d'intérêt annuel (%)</label>
              <input
                type="number"
                name="interestRate"
                value={calculatorData.interestRate}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Ex: 4.5"
                min="0"
                max="20"
                step="0.1"
                required
              />
            </div>

            {/* Boutons */}
            <div className="flex" style={{ gap: '1rem', flexWrap: 'wrap' }}>
              <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                <TrendingUp size={16} />
                Calculer
              </button>
              <button type="button" onClick={resetCalculator} className="btn-secondary">
                Réinitialiser
              </button>
            </div>
          </form>

          {/* Résultats */}
          {result && (
            <div className="result-highlight">
              <h4 className="heading-3 mb-3">Résultats de votre simulation</h4>

              {/* HERO METRIC - Intérêts cumulés mis en avant */}
              <div data-testid="calc-interest-hero" style={{
                background: 'linear-gradient(135deg, var(--primary-gold) 0%, #c9a961 50%, #d4af37 100%)',
                borderRadius: '1rem',
                padding: '1.5rem 1.25rem',
                textAlign: 'center',
                marginBottom: '1.25rem',
                boxShadow: '0 12px 32px rgba(172, 153, 105, 0.35)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <p style={{
                  color: 'rgba(255,255,255,0.95)',
                  fontWeight: '700',
                  fontSize: '0.78rem',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  margin: 0,
                  marginBottom: '0.4rem'
                }}>
                  Intérêts cumulés
                </p>
                <p style={{
                  color: 'white',
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(2rem, 5vw, 2.75rem)',
                  fontWeight: '700',
                  margin: 0,
                  lineHeight: 1.1,
                  textShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                  +{result.totalInterest.toLocaleString('fr-FR')} €
                </p>
                <p style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.8rem',
                  margin: 0,
                  marginTop: '0.4rem'
                }}>
                  soit un rendement de <strong>+{result.roi}%</strong> sur la durée
                </p>
              </div>

              <div className="grid-2" style={{ gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div className="text-center" style={{ padding: '0.75rem', background: 'rgba(58, 46, 15, 0.04)', borderRadius: '0.5rem' }}>
                  <p className="body-small text-muted" style={{ marginBottom: '0.25rem' }}>Total investi</p>
                  <p className="result-amount" style={{ fontSize: '1.1rem', margin: 0, color: 'var(--primary-brown)' }}>
                    {result.totalInvested.toLocaleString('fr-FR')} €
                  </p>
                </div>
                <div className="text-center" style={{ padding: '0.75rem', background: 'rgba(172, 153, 105, 0.08)', borderRadius: '0.5rem' }}>
                  <p className="body-small text-muted" style={{ marginBottom: '0.25rem' }}>Capital final</p>
                  <p className="result-amount" style={{ fontSize: '1.1rem', margin: 0, color: 'var(--primary-brown)' }}>
                    {result.finalAmount.toLocaleString('fr-FR')} €
                  </p>
                </div>
              </div>

              {/* Graphique */}
              <div style={{ marginBottom: '2rem' }}>
                <h5 className="heading-3" style={{ fontSize: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
                  Évolution de votre capital dans le temps
                </h5>
                <div style={{ width: '100%', height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={result.chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(172, 153, 105, 0.2)" />
                      <XAxis 
                        dataKey="year" 
                        stroke="var(--text-secondary)"
                        fontSize={12}
                        tickFormatter={(value) => `${value}ans`}
                      />
                      <YAxis 
                        stroke="var(--text-secondary)"
                        fontSize={12}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`}
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          `${value.toLocaleString('fr-FR')} €`, 
                          name === 'invested' ? 'Capital investi' : 
                          name === 'interest' ? 'Intérêts générés' : 'Capital total'
                        ]}
                        labelFormatter={(label) => `Année ${label}`}
                        contentStyle={{
                          backgroundColor: 'var(--bg-card)',
                          border: '1px solid var(--border-light)',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem'
                        }}
                      />
                      <Legend 
                        formatter={(value) => 
                          value === 'invested' ? 'Capital investi' : 
                          value === 'interest' ? 'Intérêts générés' : 'Capital total'
                        }
                        wrapperStyle={{ fontSize: '0.875rem' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="invested"
                        stackId="1"
                        stroke="#2a1f08"
                        fill="#3a2e0f"
                        fillOpacity={0.9}
                      />
                      <Area
                        type="monotone"
                        dataKey="interest"
                        stackId="1"
                        stroke="#FFD700"
                        fill="#DAA520"
                        fillOpacity={0.9}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="body-small text-center text-muted mt-2">
                  En <span style={{ color: '#3a2e0f', fontWeight: 'bold' }}>■</span> votre capital investi, 
                  en <span style={{ color: '#DAA520', fontWeight: 'bold' }}>■</span> les intérêts générés
                </p>
              </div>

              <div className="text-center">
                <p className="body-small text-muted">
                  * Simulation à titre indicatif. Les performances passées ne présagent pas des performances futures.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* CTA après calculateur */}
        <div className="text-center mt-6">
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
            <h3 className="heading-3 mb-2">Optimisez vos placements</h3>
            <p className="body-medium mb-4">
              Ces chiffres vous intéressent ? Découvrons ensemble les meilleures solutions 
              pour faire fructifier votre patrimoine.
            </p>
            <a 
              href="https://calendly.com/arthur-vacogne-rdv/rdv-echange-1er-prise-de-contact" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Consulter un expert
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;